import type { MiningRuntimeOptions, SilverdropMiningRuntime } from "../mining";

interface MiningJob {
  jobId: string;
  blob: string;
  target: string;
  algorithm: string;
  noncePrefix: string;
}

type ServerMessage =
  | { type: "ready"; sessionId: string; noncePrefix: string }
  | ({ type: "job" } & MiningJob)
  | { type: "share-result"; requestId: string; accepted: boolean; message?: string }
  | { type: "status"; connected: boolean; acceptedShares: number; informationalHashrate: number; message?: string }
  | { type: "error"; code: string; message: string };

type WorkerMessage =
  | { type: "ready" }
  | { type: "progress"; hashes: number }
  | { type: "share"; jobId: string; nonce: string; result: string }
  | { type: "exhausted"; jobId: string }
  | { type: "fatal"; message: string };

class BrowserMiningRuntime implements SilverdropMiningRuntime {
  private socket: WebSocket | null = null;
  private workers: Worker[] = [];
  private options: MiningRuntimeOptions | null = null;
  private running = false;
  private ready = false;
  private noncePrefix: string | null = null;
  private totalHashes = 0;
  private samples: Array<{ at: number; hashes: number }> = [];
  private rateTimer: number | null = null;
  private lastGatewayRateAt = 0;
  private currentJobId: string | null = null;
  private pendingShares = new Set<string>();

  async start(options: MiningRuntimeOptions): Promise<void> {
    if (this.running) throw new Error("Mining is already running");
    const gateway = new URL(options.gatewayUrl);
    if (gateway.protocol !== "wss:" || gateway.hostname !== "mine.ltcdrop.com" || gateway.pathname !== "/ws") {
      throw new Error("Silverdrop refused an unexpected mining gateway address");
    }

    this.running = true;
    this.options = options;
    this.totalHashes = 0;
    this.samples = [{ at: performance.now(), hashes: 0 }];
    this.currentJobId = null;
    this.pendingShares.clear();
    options.onStatus("Loading verified GhostRider engine…");

    try {
      const threadCount = Math.max(1, Math.min(Math.trunc(options.threads), 8));
      const cpuPercent = Math.max(10, Math.min(options.cpuPercent, 70));
      await Promise.all(Array.from({ length: threadCount }, (_, index) => this.createWorker(index, threadCount, cpuPercent)));
      if (!this.running) throw new Error("Mining was stopped before startup completed");
      options.onStatus("Verified engine loaded; connecting to the pool…");
      await this.connect(gateway.href, options.ticket);
      this.startRateReporting();
    } catch (error) {
      await this.stopInternal(false);
      throw error;
    }
  }

  async stop(): Promise<void> {
    await this.stopInternal(true);
  }

  private createWorker(index: number, count: number, cpuPercent: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const worker = new Worker(new URL("./hash-worker.ts", import.meta.url), { type: "module", name: `silverdrop-hasher-${index + 1}` });
      this.workers.push(worker);
      let initialized = false;
      const timeout = window.setTimeout(() => reject(new Error("The verified hashing engine took too long to load")), 20_000);
      worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
        if (event.data.type === "ready" && !initialized) {
          initialized = true;
          window.clearTimeout(timeout);
          resolve();
          return;
        }
        if (event.data.type === "fatal") {
          window.clearTimeout(timeout);
          const error = new Error(event.data.message);
          if (!initialized) reject(error);
          else this.runtimeFailure(error);
          return;
        }
        this.handleWorkerMessage(event.data);
      };
      worker.onerror = (event) => {
        window.clearTimeout(timeout);
        const error = new Error(event.message || "Browser hashing worker failed");
        if (!initialized) reject(error);
        else this.runtimeFailure(error);
      };
      worker.postMessage({
        type: "initialize",
        wasmUrl: new URL("/miner/ghostrider.wasm", window.location.origin).href,
        workerIndex: index,
        workerCount: count,
        cpuPercent
      });
    });
  }

  private connect(gatewayUrl: string, ticket: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(gatewayUrl);
      this.socket = socket;
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (!settled) reject(new Error("The mining gateway did not respond in time"));
        socket.close();
      }, 20_000);

      socket.onopen = () => socket.send(JSON.stringify({ type: "hello", ticket, clientVersion: "silverdrop-miner/1.0.0" }));
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as ServerMessage;
          if (message.type === "ready") {
            if (!/^[0-9a-f]{4}$/iu.test(message.noncePrefix)) throw new Error("Gateway supplied an invalid nonce partition");
            this.noncePrefix = message.noncePrefix.toLowerCase();
            this.ready = true;
            if (!settled) {
              settled = true;
              window.clearTimeout(timeout);
              resolve();
            }
            this.options?.onStatus("Connected; waiting for the pool's first job…");
            return;
          }
          this.handleServerMessage(message);
        } catch (error) {
          const parsed = error instanceof Error ? error : new Error(String(error));
          if (!settled) {
            settled = true;
            window.clearTimeout(timeout);
            reject(parsed);
          } else {
            this.runtimeFailure(parsed);
          }
        }
      };
      socket.onerror = () => {
        if (!settled) {
          settled = true;
          window.clearTimeout(timeout);
          reject(new Error("Could not connect to the mining gateway"));
        }
      };
      socket.onclose = (event) => {
        window.clearTimeout(timeout);
        if (!settled) {
          settled = true;
          reject(new Error(event.reason || "The mining gateway closed the connection"));
        } else if (this.running) {
          this.runtimeFailure(new Error(event.reason || "The mining gateway connection ended"));
        }
      };
    });
  }

  private handleServerMessage(message: ServerMessage): void {
    if (message.type === "job") {
      if (!this.ready || !this.noncePrefix) return;
      if (message.noncePrefix.toLowerCase() !== this.noncePrefix) throw new Error("Gateway changed this session's nonce partition");
      if (message.algorithm.toLowerCase() !== "ghostrider") throw new Error(`Pool requested unsupported algorithm ${message.algorithm}`);
      if (!/^[0-9a-f]{160}$/iu.test(message.blob) || !/^[0-9a-f]{8}([0-9a-f]{8})?$/iu.test(message.target)) {
        throw new Error("Pool supplied a malformed GhostRider job");
      }
      const job: MiningJob = {
        jobId: message.jobId,
        blob: message.blob.toLowerCase(),
        target: message.target.toLowerCase(),
        algorithm: "ghostrider",
        noncePrefix: this.noncePrefix
      };
      this.currentJobId = job.jobId;
      for (const worker of this.workers) worker.postMessage({ type: "job", job });
      this.options?.onStatus("Mining verified GhostRider pool work. Stop whenever you like.");
      return;
    }
    if (message.type === "share-result") {
      if (!this.pendingShares.delete(message.requestId)) return;
      if (message.accepted) {
        this.options?.onStatus("Share accepted by the pool and queued for settlement.");
        this.options?.onAcceptedShare();
      } else {
        this.options?.onStatus(message.message ? `Pool rejected a share: ${message.message}` : "Pool rejected a share.");
      }
      return;
    }
    if (message.type === "status") {
      if (message.message && !this.currentJobId) this.options?.onStatus(message.message);
      return;
    }
    if (message.type === "error") throw new Error(`${message.code}: ${message.message}`);
  }

  private handleWorkerMessage(message: WorkerMessage): void {
    if (message.type === "progress") {
      if (Number.isSafeInteger(message.hashes) && message.hashes > 0) this.totalHashes += message.hashes;
      return;
    }
    if (message.type === "share") {
      if (!this.running || this.socket?.readyState !== WebSocket.OPEN || message.jobId !== this.currentJobId) return;
      if (!/^[0-9a-f]{8}$/u.test(message.nonce) || !message.nonce.startsWith(this.noncePrefix ?? "") || !/^[0-9a-f]{64}$/u.test(message.result)) {
        this.runtimeFailure(new Error("Hashing worker produced an invalid share envelope"));
        return;
      }
      const requestId = crypto.randomUUID().replaceAll("-", "");
      this.pendingShares.add(requestId);
      this.socket.send(JSON.stringify({ type: "submit", requestId, jobId: message.jobId, nonce: message.nonce, result: message.result }));
      return;
    }
    if (message.type === "exhausted" && message.jobId === this.currentJobId) {
      this.options?.onStatus("This pool job's assigned nonce range is complete; waiting for new work…");
    }
  }

  private startRateReporting(): void {
    this.rateTimer = window.setInterval(() => {
      const now = performance.now();
      this.samples.push({ at: now, hashes: this.totalHashes });
      while (this.samples.length > 2 && (this.samples[1]?.at ?? now) < now - 5_000) this.samples.shift();
      const first = this.samples[0] ?? { at: now, hashes: this.totalHashes };
      const elapsed = Math.max((now - first.at) / 1_000, 0.001);
      const rate = Math.max(0, (this.totalHashes - first.hashes) / elapsed);
      this.options?.onHashrate(rate);
      if (this.socket?.readyState === WebSocket.OPEN && now - this.lastGatewayRateAt >= 10_000) {
        this.socket.send(JSON.stringify({ type: "hashrate", hashesPerSecond: rate }));
        this.lastGatewayRateAt = now;
      }
    }, 1_000);
  }

  private runtimeFailure(error: Error): void {
    if (!this.running) return;
    const onStopped = this.options?.onStopped;
    this.options?.onStatus(`${error.message}. Mining stopped.`);
    void this.stopInternal(false).finally(() => onStopped?.(error.message));
  }

  private async stopInternal(reportStatus: boolean): Promise<void> {
    const options = this.options;
    this.running = false;
    this.ready = false;
    this.noncePrefix = null;
    this.currentJobId = null;
    this.pendingShares.clear();
    if (this.rateTimer !== null) window.clearInterval(this.rateTimer);
    this.rateTimer = null;
    for (const worker of this.workers) {
      worker.postMessage({ type: "stop" });
      worker.terminate();
    }
    this.workers = [];
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close(1000, "User stopped mining");
    }
    this.socket = null;
    this.options = null;
    options?.onHashrate(0);
    if (reportStatus) options?.onStatus("Mining stopped.");
  }
}

window.SilverdropMiningRuntime = new BrowserMiningRuntime();
